import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { User } from '../../../_models/User';
import { Vendor, VendorUser } from '../../../_models/fundraising/Vendor';
import { DialogService } from '../../../_services/dialog.service';
import { VendorService } from '../../../_services/fundraising/vendor.service';
import { UserService } from '../../../_services/user.service';
import { EngineService } from '../../../_services/engine.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-vendor-create',
  templateUrl: './vendor-user-create.component.html',
  styleUrls: ['../vendor/vendor-create.component.css']
})
export class VendorUserCreateComponent implements OnInit {

  public vendorUserForm: FormGroup;

  public users: User[] = [];
  public vendors: Vendor[] = [];

  public enableSubscription = false;
  public validationErrors = '';

  constructor(
    private vendorService: VendorService,
    private userService: UserService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef
  ) { }

  ngOnInit(): void {

    this.spinner.show();

    const vendorObs = this.vendorService.getAll();
    const userServiceObs = this.userService.getAllUsers();

      forkJoin([userServiceObs, vendorObs])
        .subscribe(
            data => {

                this.users = data[0];
                this.vendors = data[1].filter(vu => vu.primaryContact).map(vu => vu.vendor);

                this.createForm(null);
                this.spinner.hide();
            },
            error => {
            this.dialogService.alert('Oops something went wrong..', error.error, this.viewRef);
            }
        );

    

  }

  createForm(vu: VendorUser) {
    this.vendorUserForm = this.fb.group({
      vendorId: [vu?.vendor.vendorId || 0, [Validators.required, Validators.min(1)]],
      active: [vu?.vendor.active],
      enableSubscriptionPeriod: [false],
      userId : [vu?.user.userId || 0],
      userFirstName: [vu?.user.firstName, Validators.required],
      userLastName: [vu?.user.lastName, Validators.required],
      userEmail: [vu?.user.email, Validators.required],
      canProgramCustomTracks: { value: vu?.user.canProgramCustomTracks, disabled: vu?.user.userId > 0 },//[vu?.user.canProgramCustomTracks],
      enableNetworkSync: {value: vu?.user.enableNetworkSync, disabled: vu?.user.userId > 0 },
      canEditVenues: {value: vu?.user.canEditVenues, disabled: vu?.user.userId > 0 },
      userPhoneNumber: [vu?.user.phoneNumber, Validators.required],
      period: { value: { startDate: new Date(), endDate: new Date(), isNew: false }, disabled: vu?.user.userId > 0  },
    });

    this.setUserPeriod(vu?.user);
  }

  selectUser(userId) {
   const user = this.users.find(u => u.userId === +userId);
    if (user) {
      this.vendorUserForm.controls.userLastName.patchValue(user.lastName);
      this.vendorUserForm.controls.userFirstName.patchValue(user.firstName);
      this.vendorUserForm.controls.userEmail.patchValue(user.email);
      this.vendorUserForm.controls.canProgramCustomTracks.patchValue(user.canProgramCustomTracks);
      this.vendorUserForm.controls.canEditVenues.patchValue(user.canEditVenues);
      this.vendorUserForm.controls.enableNetworkSync.patchValue(user.enableNetworkSync);
      this.vendorUserForm.controls.userPhoneNumber.patchValue(user.phoneNumber);

      this.vendorUserForm.controls.canProgramCustomTracks.disable();
      this.vendorUserForm.controls.canEditVenues.disable();
      this.vendorUserForm.controls.enableNetworkSync.disable();
      this.vendorUserForm.controls.userEmail.disable();
      this.vendorUserForm.controls.enableSubscriptionPeriod.disable();
      this.setUserPeriod(user);
      this.vendorUserForm.updateValueAndValidity();
    } else {
      this.vendorUserForm.controls.userLastName.patchValue('');
      this.vendorUserForm.controls.userFirstName.patchValue('');
      this.vendorUserForm.controls.userEmail.patchValue('');
      this.vendorUserForm.controls.canProgramCustomTracks.patchValue(false);
      this.vendorUserForm.controls.canEditVenues.patchValue(false);
      this.vendorUserForm.controls.enableNetworkSync.patchValue(false);
      this.vendorUserForm.controls.userPhoneNumber.patchValue('');

      this.vendorUserForm.controls.canProgramCustomTracks.enable();
      this.vendorUserForm.controls.canEditVenues.enable();
      this.vendorUserForm.controls.enableNetworkSync.enable();
      this.vendorUserForm.controls.userEmail.enable();
      this.vendorUserForm.controls.period.enable();
      this.vendorUserForm.controls.enableSubscriptionPeriod.enable();
      this.enableSubscription = false;
      this.vendorUserForm.controls.enableSubscriptionPeriod.patchValue(false);
      this.vendorUserForm.updateValueAndValidity();
    }
  }

  setUserPeriod(user: User) {
    if (user && user.subscriptionStartDate && user.subscriptionEndDate) {
      this.vendorUserForm.controls.period.patchValue({
        startDate: new Date(user.subscriptionStartDate),
        endDate: new Date(user.subscriptionEndDate)
    });
      this.enableSubscription = true;
      this.vendorUserForm.controls.enableSubscriptionPeriod.patchValue(true);
      this.vendorUserForm.controls.period.disable();
    } else {
      this.enableSubscription = false;
      this.vendorUserForm.controls.enableSubscriptionPeriod.patchValue(false);
      this.vendorUserForm.controls.period.enable();
    }
  }

  save() {
    this.spinner.show();

    this.vendorUserForm.controls.userEmail.enable();
    this.vendorUserForm.controls.userEmail.updateValueAndValidity();

    if (!this.vendorUserForm.valid) {

      this.vendorUserForm.markAllAsTouched();
      this.vendorUserForm.updateValueAndValidity();
      this.validationErrors = 'Please complete the required fields';

      if (this.vendorUserForm.controls.userId.value > 0) {

        this.vendorUserForm.controls.userEmail.disable();
      }

      this.spinner.hide();
      return;
    }

    const user = this.users.find(u => u.email.replace(/\s/g, "").toLowerCase() === this.vendorUserForm.controls.userEmail.value.replace(/\s/g, "").toLowerCase());
    if (user && this.vendorUserForm.controls.userId.value < 1) {
      this.validationErrors = 'A user with that email address has already been created';
      this.spinner.hide();
      return;
    }

    this.validationErrors = '';

    const vendorUserData = {
      vendorId: this.vendorUserForm.controls.vendorId.value,
      userId: this.vendorUserForm.controls.userId.value,
      userInfo: { 
        email: this.vendorUserForm.controls.userEmail.value.replace(/\s/g, "").toLowerCase(),
        canProgramCustomTracks: this.vendorUserForm.controls.canProgramCustomTracks.value,
        phoneNumber : this.vendorUserForm.controls.userPhoneNumber.value,
        subscriptionStartDate: this.vendorUserForm.controls.period.value.startDate,
        subscriptionEndDate: this.vendorUserForm.controls.period.value.endDate, 
        canEditVenues: this.vendorUserForm.controls.canEditVenues.value,  
        enableNetworkSync: this.vendorUserForm.controls.enableNetworkSync.value,
        firstName: this.vendorUserForm.controls.userFirstName.value,
        lastName: this.vendorUserForm.controls.userLastName.value
      }
    };

    this.vendorService.createVendorUser(vendorUserData).subscribe(
        () => { 
            this.spinner.hide();
            this.dialogService.alert("Success!", "Successfully created a new vendor user.", this.viewRef)
            .then(
                _ => {
                    this.navigateUp();
                }
            );
        },
        error => {
            this.spinner.hide();
            this.dialogService.alert("Unable to create vendor user", error.error, this.viewRef);
        }
    );
  }

  public navigateUp() {
    this.router.navigate(['../'], {relativeTo: this.route});
  }

}
